import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, AlertCircle, TrendingUp, Gauge } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';

/**
 * PacingBanner — surfaces the Activity Pacing Engine v1 state.
 *
 * Adaptive Mode only. Polls /api/pacing on mount + when refreshKey changes.
 * Renders:
 *  - flare-hold variant (clay tone, gentle copy)
 *  - normal target variant (sage tone, +10% baseline)
 *  - cold-start variant (waiting for data)
 *
 * Honours the Anti-Shame Policy — "Your body is asking for less right now"
 * never "you're behind on your goal."
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PacingBanner = ({ refreshKey = 0 }) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const isAdaptive = !!profile?.adaptiveMode;
  const [pacing, setPacing] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        // Pass the user's TZ offset so the pacing engine can floor "this
        // week" against their local Monday, not UTC's. getTimezoneOffset()
        // returns +480 for PST, -120 for CEST, etc.
        const tzOffset = new Date().getTimezoneOffset();
        const res = await axios.get(`${API}/pacing`, { params: { tz_offset_minutes: tzOffset } });
        if (!cancelled) setPacing(res.data);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user, refreshKey]);

  if (!user || error || !pacing) return null;

  const flareHold = pacing.flare_hold;
  const baseline = pacing.baseline_min;
  const target = pacing.target_min;
  const thisWeek = pacing.this_week_min;
  const pctDone = target > 0 ? Math.min(100, Math.round((thisWeek / target) * 100)) : 0;

  // Cold-start: no baseline yet — show calibration state, no shame
  if (baseline === 0) {
    return (
      <div
        className="rounded-xl p-5 border border-enf-surface-border bg-enf-surface-deep"
        data-testid="pacing-banner-coldstart"
      >
        <div className="flex items-start gap-3">
          <Activity className="w-5 h-5 text-enf-text-secondary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-enf-text-muted font-semibold mb-1">
              Pacing · calibrating
            </div>
            <div className="text-base text-enf-text-primary leading-relaxed">
              {pacing.why_this_pace}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Adaptive Mode uses the warm sand palette; Athlete Mode uses standard app tones.
  const tone = isAdaptive
    ? (flareHold ? 'border-adaptive-clay/40 bg-adaptive-clay/5' : 'border-adaptive-sage/40 bg-adaptive-sage/5')
    : (flareHold ? 'border-enf-role-warning/40 bg-enf-role-warning/5' : 'border-enf-role-info/40 bg-enf-role-info/5');
  const Icon = flareHold ? AlertCircle : TrendingUp;
  const iconClass = isAdaptive
    ? (flareHold ? 'text-adaptive-clay' : 'text-adaptive-sage')
    : (flareHold ? 'text-enf-role-warning' : 'text-enf-role-info');
  const label = flareHold ? 'Pacing · holding load' : 'Pacing · steady build';

  return (
    <div className={`rounded-xl p-5 border ${tone}`} data-testid="pacing-banner">
      <div className="flex items-start gap-3 mb-4">
        <Icon className={`w-5 h-5 ${iconClass} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className={`text-[10px] uppercase tracking-[0.3em] font-semibold mb-1 ${iconClass}`}>
            {label}
          </div>
          <div className="text-base text-enf-text-primary leading-relaxed">
            {pacing.why_this_pace}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">This week</div>
          <div className="font-display text-3xl tracking-wide leading-none">{thisWeek}<span className="text-base text-enf-text-muted ml-1">min</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Target</div>
          <div className={`font-display text-3xl tracking-wide leading-none ${iconClass}`}>
            {target}<span className="text-base text-enf-text-muted ml-1">min</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Baseline</div>
          <div className="font-display text-3xl tracking-wide leading-none text-enf-text-secondary">{baseline}<span className="text-base text-enf-text-muted ml-1">min</span></div>
        </div>
      </div>

      <div className="mt-4 h-1.5 bg-enf-surface-mid rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-700 ${isAdaptive ? (flareHold ? 'bg-adaptive-clay' : 'bg-adaptive-sage') : (flareHold ? 'bg-enf-role-warning' : 'bg-enf-role-info')}`}
          style={{ width: `${pctDone}%` }}
        />
      </div>
      <div className="mt-2 text-xs text-enf-text-muted">
        {pctDone}% of this week's target · {pacing.training_weeks_observed}-week baseline
      </div>

      {/* v2: Capacity score — unified read of subjective check-in + HealthKit signals. */}
      {pacing.capacity_score !== null && pacing.capacity_score !== undefined && (
        <div
          className="mt-4 pt-4 border-t border-enf-surface-border flex items-center gap-3"
          data-testid="pacing-capacity"
        >
          <Gauge className="w-4 h-4 text-enf-role-info flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-enf-text-muted font-semibold">
              Today's capacity · {pacing.capacity?.source || 'subjective'}
            </div>
            <div className="text-sm text-enf-text-primary leading-snug mt-0.5">
              <span className="font-display text-lg tracking-wide">{pacing.capacity_score}</span>
              <span className="text-enf-text-muted text-xs ml-1">/100</span>
              <span className="text-enf-text-secondary ml-2">· {pacing.capacity?.capacity_reason}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PacingBanner;
